<template>
  <div class="surface-50 px-3 py-6 md:px-5">
    <Card class="w-full md:w-8 lg:w-6 xl:w-5 mx-auto shadow-1">
      <template #content>
        <div class="text-center mb-5">
          <div
            class="bg-green text-white border-circle flex align-items-center justify-content-center mx-auto mb-3"
            style="width: 3.5rem; height: 3.5rem"
          >
            <i class="pi pi-envelope text-2xl text-white" aria-hidden="true" />
          </div>
          <h2 class="text-2xl md:text-3xl text-900 mt-0 mb-2">
            Subscribe to {{ subscriptionLabel }}
          </h2>
          <p class="text-600 line-height-3 m-0">
            {{ subscriptionDescription }}
          </p>
        </div>

        <form class="grid" @submit.prevent="subscribe">
          <div class="hidden" aria-hidden="true">
            <label :for="fieldIds.category">Category</label>
            <input
              :id="fieldIds.category"
              v-model="body.category"
              name="category"
              type="text"
              tabindex="-1"
              autocomplete="off"
            />
          </div>

          <div class="col-12 md:col-6 flex flex-column gap-2">
            <label :for="fieldIds.firstName" class="font-medium text-900">First name</label>
            <InputText
              :id="fieldIds.firstName"
              v-model.trim="body.attributes.FIRSTNAME"
              name="first_name"
              autocomplete="given-name"
              class="w-full"
              required
            />
          </div>

          <div class="col-12 md:col-6 flex flex-column gap-2">
            <label :for="fieldIds.lastName" class="font-medium text-900">Last name</label>
            <InputText
              :id="fieldIds.lastName"
              v-model.trim="body.attributes.LASTNAME"
              name="last_name"
              autocomplete="family-name"
              class="w-full"
              required
            />
          </div>

          <div class="col-12 flex flex-column gap-2">
            <label :for="fieldIds.email" class="font-medium text-900">Email address</label>
            <InputText
              :id="fieldIds.email"
              v-model.trim="body.email"
              name="email"
              type="email"
              autocomplete="email"
              class="w-full"
              required
            />
          </div>

          <TurnstileWidget
            ref="turnstileWidget"
            v-model="turnstileToken"
            action="subscribe_form"
            class="col-12 mt-2"
          />

          <div class="col-12 mt-2">
            <Button
              class="w-full bg-green border-none"
              :label="`Subscribe to ${subscriptionLabel}`"
              icon="pi pi-envelope"
              iconPos="right"
              type="submit"
              :loading="isSubmitting"
              :disabled="!turnstileToken"
            />
          </div>
        </form>

        <div class="flex align-items-center justify-content-center gap-2 text-xs text-500 mt-3">
          <i class="pi pi-lock" aria-hidden="true" />
          <span>You can unsubscribe at any time. We will not share your email address.</span>
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { useToast } from 'primevue/usetoast'

const props = defineProps({
  listId: {
    type: Number,
    required: true,
    validator: (value) => Number.isSafeInteger(value) && value > 0,
  },
})

const toast = useToast()
const isSubmitting = ref(false)
const turnstileWidget = ref(null)
const turnstileToken = ref('')
const componentId = `subscription-${useId().replace(/[^a-zA-Z0-9_-]/g, '-')}`
const fieldIds = {
  category: `${componentId}-category`,
  firstName: `${componentId}-first-name`,
  lastName: `${componentId}-last-name`,
  email: `${componentId}-email`,
}

const listEndpoint = computed(() => `/api/email/lists/${props.listId}`)
const { data: subscriptionList } = await useFetch(listEndpoint, {
  default: () => null,
})

const subscriptionLabel = computed(() => {
  const listName = subscriptionList.value?.name
  return typeof listName === 'string' && listName.trim() ? listName.trim() : 'SATS updates'
})
const subscriptionDescription =
  'Stay informed about the latest news, resources, and events from SATS.'

const body = ref({
  attributes: {
    FIRSTNAME: '',
    LASTNAME: '',
  },
  email: '',
  category: '',
})

const subscribe = async () => {
  if (isSubmitting.value) return

  if (!turnstileToken.value) {
    toast.add({
      severity: 'warn',
      summary: 'Verification required',
      detail: 'Please complete the security verification before subscribing.',
      life: 5000,
    })
    return
  }

  isSubmitting.value = true

  try {
    await $fetch('/api/email/subscribe', {
      method: 'POST',
      body: {
        ...body.value,
        listIds: [props.listId],
        turnstile_token: turnstileToken.value,
      },
    })

    toast.add({
      severity: 'success',
      summary: 'Subscription confirmed',
      detail: `You have successfully subscribed to ${subscriptionLabel.value}.`,
      life: 3000,
    })

    body.value.email = ''
    body.value.attributes.FIRSTNAME = ''
    body.value.attributes.LASTNAME = ''
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Subscription unsuccessful',
      detail: 'We could not complete your subscription. Please try again.',
      life: 5000,
    })
  } finally {
    turnstileWidget.value?.reset()
    isSubmitting.value = false
  }
}
</script>
